import {ChangeDetectorRef, Component, Input} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {ToastrService} from 'ngx-toastr';
import {DATE_MAX} from '../../../../app.constants';
import {Destroyable} from '../../../../destroyable.component';
import {EmpContractAttributes} from '../../../../models/models.interfaces';
import {Data} from '../../../api';
import {EmployeesUtilsService} from '../../../employees/employees-utils.service';
import {PayPeriodContract} from '../../../employees/employees.model';
import {EmployeesService} from '../../../employees/employees.service';
import {ModalComponent} from '../../../modal';
import {DateLocalizationPipe} from '../../../pipes/date-localization.pipe';

interface TranslateKeys {
  title: string;
  text: string;
  success: string;
}

@Component({
  selector: 'gp-delete-contract-modal',
  templateUrl: './delete-contract-modal.component.html',
  styleUrls: ['./delete-contract-modal.component.scss']
})
export class DeleteContractModalComponent extends Destroyable {

  public translateKeys = new Map<string, TranslateKeys>([
    [
      'single', {
      title: 'DIALOG.confirm_delete_contract',
      text: 'DIALOG.confirm_delete_contract?',
      success: 'DIALOG.success_delete_contract'
    }
    ],
    [
      'plurals', {
      title: 'DIALOG.confirm_delete_contracts',
      text: 'DIALOG.confirm_delete_contracts?',
      success: 'DIALOG.success_delete_contracts'
    }
    ],
    [
      'deleteEmployee', {
      title: 'DIALOG.confirm_delete_contracts_and_employee',
      text: 'DIALOG.confirm_delete_contracts_and_employee?',
      success: 'DIALOG.success_delete_contracts_and_employee'
    }
    ]
  ]);

  @Input() public contracts: Array<Data<EmpContractAttributes>> = [];

  @Input() public employeeId: number = null;

  public payPeriodContracts: PayPeriodContract[] = [];

  public selectedContracts: number[] = [];

  public deleteEmployeeId: number = null;

  public step: 'select' | 'confirm' | 'conflict' = 'select';

  public selectedTranslateKeys: TranslateKeys = this.translateKeys.get('single');

  public deleteIsLoading = false;

  constructor(private dateLocalizationPipe: DateLocalizationPipe,
              private employeeService: EmployeesService,
              private toastrService: ToastrService,
              private translate: TranslateService,
              private employeesUtilsService: EmployeesUtilsService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public isSelectStep(): boolean {
    return this.isStep('select');
  }

  public isConfirmStep(): boolean {
    return this.isStep('confirm');
  }

  public isConflictStep(): boolean {
    return this.isStep('conflict');
  }

  public formatContractDates(contract: Data<EmpContractAttributes>): string {
    return `(${
      this.dateLocalizationPipe.transform(contract.begindate)
    } - ${
      contract.enddate === DATE_MAX || !contract.enddate
        ? '...'
        : this.dateLocalizationPipe.transform(contract.enddate)
    })`;
  }

  public deleteContracts(): void {
    this.deleteEmployeeId = this.selectedContracts.length === this.contracts.length ? this.employeeId : null;
    this.selectTranslateKeys();
    this.step = 'confirm';
  }

  public confirmDeleteContracts(modal: ModalComponent): void {
    this.deleteIsLoading = true;
    this.employeeService.deleteContractsById(this.selectedContracts).subscribe(
      () => {
        this.deleteIsLoading = false;
        this.handleDeletionSuccess(modal);
      },
      () => {
        this.deleteIsLoading = false;
        this.step = 'conflict';
        this.cd.detectChanges();
      });
  }

  public toggleContract(contract: Data<EmpContractAttributes>) {
    const indexOf = this.selectedContracts.indexOf(contract.id);
    if (indexOf === -1) {
      this.selectedContracts.push(contract.id);
    } else {
      this.selectedContracts.splice(indexOf, 1);
    }
  }

  public close(modal: ModalComponent) {
    this.step = 'select';
    this.selectedContracts = [];
    this.deleteEmployeeId = null;
    modal.close();
  }

  private isStep(step: 'select' | 'confirm' | 'conflict'): boolean {
    return this.step === step;
  }

  private selectTranslateKeys() {
    let key;
    if (this.deleteEmployeeId) {
      key = 'deleteEmployee';
    } else {
      key = this.selectedContracts.length > 1 ? 'plurals' : 'single';
    }

    this.selectedTranslateKeys = this.translateKeys.get(key);
  }

  private handleDeletionSuccess(modal: ModalComponent): void {
    modal.close();
    this.toastrService.success(this.translate.instant(this.selectedTranslateKeys.success));
    if (this.deleteEmployeeId) {
      this.employeesUtilsService.employeeDeleted$.next(this.deleteEmployeeId);
    } else {
      this.employeesUtilsService.contractsDeleted$.next(this.selectedContracts);
    }
  }
}
